import { prizeContract } from '@daymade/api-contracts';
import { ClientInferResponseBody } from '@ts-rest/core';
import { action, makeObservable, observable } from 'mobx';
import { LandingPagePrize, Prize } from 'types/Prize';
import { doABTestWithDistribution } from 'utils/analytics/abtest';
import { tryApi } from 'utils/api/common';
import { prizeApiClient } from 'utils/apiClient';
import HttpStatusCode from 'utils/HttpStatusCode';
import { RootStore } from './RootStore';

export type PrizeHydration = {
  dashboardPrizes?: Prize[];
  landingPagePrizes?: LandingPagePrize[];
};

const DASHBOARD_PRIZES_CACHE_TTL = 7 * 24 * 60 * 60 * 1000; // 1 week in milliseconds
const SELECTED_DASHBOARD_PRIZE_TTL = 5 * 60 * 1000; // 5 minutes in milliseconds
const DASHBOARD_PRIZES_CACHE_KEY = 'dashboardPrizesCache';
const SELECTED_PRIZE_CACHE_KEY = 'selectedDashboardPrizeCache';

type TDashboardPrize = ClientInferResponseBody<
  typeof prizeContract.public.getDashboardPrizes,
  200
>[number];

export class PrizeStore {
  root: RootStore;
  dashboardPrizes?: TDashboardPrize[] = [];
  dashboardPrizeSelected?: null | TDashboardPrize = null;
  landingPagePrizes: LandingPagePrize[] | null = null;
  selectedLandingPagePrize: LandingPagePrize | null = null;

  constructor(root: RootStore) {
    this.root = root;

    makeObservable(this, {
      dashboardPrizes: observable,
      dashboardPrizeSelected: observable,
      landingPagePrizes: observable,
      selectedLandingPagePrize: observable,
      setLandingPagePrizeFromUTMContent: action,
    });

    this.initAsync();
  }

  initAsync = async () => {
    if (typeof window === 'undefined') {
      return;
    }
    await this.fetchDashboardPrizes();
    this.selectDashboardPrize();
  };

  getLandingPagePrizeFromUTMContent = (utmContent: string) => {
    const prizeResults = this.landingPagePrizes?.filter((prize) =>
      utmContent.includes(prize.utmContent)
    );

    if (!prizeResults || prizeResults.length === 0) {
      return;
    }

    return prizeResults[0];
  };

  setLandingPagePrizeFromUTMContent = (utmContent: string) => {
    const prizeFound = this.getLandingPagePrizeFromUTMContent(utmContent);

    if (prizeFound) {
      const testGroup = doABTestWithDistribution('DYNAMIC_LANDING_TEST_V1', [
        { choice: 'CONTROL', weight: 1 },
        { choice: 'TEST', weight: 0 },
      ]);

      this.root.abTestStore.updateABTestValuesFromLocalStorage();

      if (testGroup === 'CONTROL') {
        return;
      }

      this.selectedLandingPagePrize = prizeFound;
    }
  };

  protected fetchDashboardPrizes = async () => {
    if (typeof window === 'undefined') {
      return;
    }

    const now = new Date().getTime();
    const cachedData = localStorage.getItem(DASHBOARD_PRIZES_CACHE_KEY);

    // Check if data exists and is still valid
    if (cachedData) {
      const { data, expiration } = JSON.parse(cachedData);
      if (data && data?.successful !== false && now < expiration) {
        this.setDashboardPrizes(data);
        return;
      }
    }

    // Cache is expired or doesn't exist, fetch from API
    const dashboardPrizesResult = await tryApi(
      prizeApiClient.public.getDashboardPrizes()
    );

    if (dashboardPrizesResult.status !== HttpStatusCode.OK) {
      return;
    }

    this.setDashboardPrizes(dashboardPrizesResult.body);

    // Save fetched data and expiration to localStorage
    const newCache = {
      data: dashboardPrizesResult.body,
      expiration: now + DASHBOARD_PRIZES_CACHE_TTL,
    };

    localStorage.setItem(DASHBOARD_PRIZES_CACHE_KEY, JSON.stringify(newCache));
  };

  setDashboardPrizes = (prizes: TDashboardPrize[]) => {
    this.dashboardPrizes = prizes;
  };

  selectDashboardPrize = () => {
    const now = new Date().getTime();
    const cachedPrizeData = localStorage.getItem(SELECTED_PRIZE_CACHE_KEY);

    // If a cached prize exists and is still valid, use it
    if (cachedPrizeData) {
      const { prize, expiration } = JSON.parse(cachedPrizeData);
      if (now < expiration) {
        this.dashboardPrizeSelected = prize;
        return;
      }
    }

    // If no valid prize in cache, select a new one randomly from dashboardPrizes
    if (this.dashboardPrizes && this.dashboardPrizes.length > 0) {
      const randomIndex = Math.floor(
        Math.random() * this.dashboardPrizes.length
      );
      const newSelectedPrize = this.dashboardPrizes[randomIndex];

      this.dashboardPrizeSelected = newSelectedPrize;

      // Save the new selected prize with a new expiration of 12 hours
      const newCache = {
        prize: newSelectedPrize,
        expiration: now + SELECTED_DASHBOARD_PRIZE_TTL,
      };
      localStorage.setItem(SELECTED_PRIZE_CACHE_KEY, JSON.stringify(newCache));
    }
  };

  hydrate(data?: PrizeHydration) {
    if (data && data.dashboardPrizes) {
      this.dashboardPrizes = data.dashboardPrizes;
    }
    if (data && data.landingPagePrizes) {
      this.landingPagePrizes = data.landingPagePrizes;
    }
  }
}
